import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  ConfigurationUploadState,
  updateConfigurationUploadModalState,
  updateShowConfigurationUploadModal,
  updateShowOtaDownloadModal,
} from "@src/reducers/your-reducer";
import { getConfigurationsUseCase } from "@src/cloud-connect/ota-update/use-cases/get-configurations";
import { getConfigurationBackupsUseCase } from "@src/cloud-connect/ota-update/use-cases/get-configuration-backups";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { CONFIGURATION_TABS } from "@src/cloud-connect/ota-update/constants";
import {
  ListItem,
  OtaBackupDrawerInterface,
  OtaDrawerDataInterface,
  TabType,
} from "@src/types/ota-update";

const INITIAL_CONFIGURATION_VALUES: OtaDrawerDataInterface = {
  file: null,
  version: "",
  name: "",
  release_notes_intro: "",
  release_notes_improvements: "",
  release_notes_fixes: "",
  release_notes_general: "",
};

export const useOtaConfigurations = () => {
  const dispatch = useDispatch();

  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [configurationValues, setConfigurationValues] =
    useState<OtaDrawerDataInterface>(INITIAL_CONFIGURATION_VALUES);
  const [backupInputValues, setBackupInputValues] =
    useState<OtaBackupDrawerInterface>({
      version: "",
      backup_name: "",
      backup_notes: "",
    });
  const [activeOTAHistoryTab, setActiveOTAHistoryTab] = useState<TabType>(
    CONFIGURATION_TABS.CONFIGURATIONS,
  );
  const [configurations, setConfigurations] = useState<ListItem[] | []>([]);
  const [backups, setBackups] = useState<ListItem[] | []>([]);
  const [isConfigurationLoading, setIsConfigurationLoading] =
    useState<boolean>(true);
  const [isBackupLoading, setIsBackupLoading] = useState<boolean>(false);
  const [installUpdateId, setInstallUpdateId] = useState<string | null>(null);

  const showOtaConfigurationDrawerModal = useSelector(
    (state: RootState) =>
      state.yourSlice.modals.otaConfigurationDrawerModal.showModal,
  );
  const showConfigurationUploadModal = useSelector(
    (state: RootState) =>
      state.yourSlice.configurationUploadModal.showConfigurationUploadModal,
  );
  const showConfigurationInstallModal = useSelector(
    (state: RootState) =>
      state.yourSlice.configurationInstallModal.showConfigurationInstallModal,
  );
  const showOtaConfigurationBackupDrawerModal = useSelector(
    (state: RootState) =>
      state.yourSlice.modals.otaConfigurationBackupDrawerModal.showModal,
  );
  const showOtaDownloadModal = useSelector(
    (state: RootState) => state.yourSlice.otaDownloadModal.showOtaDownloadModal,
  );
  const showConfigurationBackupInstallModal = useSelector(
    (state: RootState) =>
      state.yourSlice.configurationBackupInstallModal
        .showConfigurationBackupInstallModal,
  );

  const fetchConfigurations = async () => {
    setIsConfigurationLoading(true);
    try {
      const result = await getConfigurationsUseCase();
      if (result.success && result.data) {
        setConfigurations(result.data);
      } else {
        console.error("Error loading list:", result.error);
        errorSnackbar(result.error, 5000);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      errorSnackbar("Something went wrong", 5000);
    } finally {
      setIsConfigurationLoading(false);
    }
  };

  const fetchBackups = async () => {
    setIsBackupLoading(true);
    try {
      const result = await getConfigurationBackupsUseCase();
      if (result.success && result.data) {
        setBackups(result.data);
      } else {
        errorSnackbar(result.error, 5000);
      }
    } catch (error) {
      errorSnackbar("Something went wrong", 5000);
    } finally {
      setIsBackupLoading(false);
    }
  };

  const closeConfigurationUploadModal = () => {
    fetchConfigurations();
    setConfigurationValues(INITIAL_CONFIGURATION_VALUES);
    dispatch(
      updateConfigurationUploadModalState(
        ConfigurationUploadState.CONFIGURATION_UPLOAD_STARTED,
      ),
    );
    dispatch(updateShowConfigurationUploadModal(false));
  };

  const onInstallStarted = (updateId: string) => {
    setInstallUpdateId(updateId);
  };

  const handleCloseOtaDownloadModal = () => {
    dispatch(updateShowOtaDownloadModal(false));
  };

  useEffect(() => {
    const el = document.getElementById("modals-entry-point");
    setModalRoot(el);
  }, []);

  useEffect(() => {
    if (activeOTAHistoryTab === CONFIGURATION_TABS.CONFIGURATIONS)
      fetchConfigurations();
    if (activeOTAHistoryTab === CONFIGURATION_TABS.BACKUPS) fetchBackups();
  }, [activeOTAHistoryTab]);

  const activeList =
    activeOTAHistoryTab === CONFIGURATION_TABS.CONFIGURATIONS
      ? configurations
      : backups;
  const isLoading =
    activeOTAHistoryTab === CONFIGURATION_TABS.CONFIGURATIONS
      ? isConfigurationLoading
      : isBackupLoading;

  return {
    modalRoot,
    configurationValues,
    setConfigurationValues,
    backupInputValues,
    setBackupInputValues,
    activeOTAHistoryTab,
    setActiveOTAHistoryTab,
    configurations,
    backups,
    activeList,
    isLoading,
    installUpdateId,
    setInstallUpdateId,
    showOtaConfigurationDrawerModal,
    showConfigurationUploadModal,
    showConfigurationInstallModal,
    showOtaConfigurationBackupDrawerModal,
    showOtaDownloadModal,
    showConfigurationBackupInstallModal,
    fetchConfigurations,
    fetchBackups,
    closeConfigurationUploadModal,
    onInstallStarted,
    handleCloseOtaDownloadModal,
    setIsConfigurationLoading,
    setIsBackupLoading,
  };
};
